import { useMemo } from 'react';
import { useWorkflow } from '../workflow/hooks';
import { groupItemsByShipment } from '../utils/item';
import type { ShipmentGroup } from '../utils/item';

/**
 * Custom hook that provides stable shipment groups with memoized computation.
 * Uses stable dependencies to prevent unnecessary re-renders.
 */
export const useShipmentGroups = (): ShipmentGroup[] => {
  const { order } = useWorkflow();

  // Memoize shipment groups with stable dependencies
  // Only recompute when shipments or items actually change
  return useMemo(() => {
    if (!order) return [];
    return groupItemsByShipment(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.shipments, order?.items]);
};

/**
 * Custom hook that provides shipment metadata for efficient lookups.
 * Returns primitives that can be passed to children without causing re-renders.
 */
export const useShipmentMetadata = () => {
  const shipmentGroups = useShipmentGroups();

  return {
    shipmentGroups,
    hasMultipleShipments: shipmentGroups.length > 1,
  };
};
